/**
 * One-shot renderer for a static tile map with 2.5D wall stacking.
 * Spawns 1-N sprites per tile depending on its stack height (LAYERS in
 * style.ts). Non-Void tiles keep a per-sprite reference in fgByIdx so
 * callers (Renderer) can re-tint the ground glyph for sound hue.
 *
 * Depth sort: sprites get zIndex = tile_y * ZY_STRIDE + local_layer so
 * a Container with sortableChildren=true draws back-to-front. Raised
 * layers of a wall get higher local zIndex than the ground, so they
 * layer on top of both their own base AND anything else in the same row.
 *
 * See CLAUDE.md architecture rule 3 (glyph atlas — no fillText).
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { tileAt, type MapData } from '../sim/map';
import { defaultBg, defaultFg, TileType } from '../sim/tiles';
import type { GlyphAtlas } from './atlas';
import { BLOOM_ALPHA, BLOOM_SCALE, LAYERS, STACK_ALPHA_GROUND, STACK_ALPHA_RAISED, STACK_DARKEN_PER_LAYER, STACK_HALO_BOOST } from './style';

/** Multiplier bumped by ROW to make depth-sort deterministic. */
export const ZY_STRIDE = 32;

export interface MapSprites {
  /** Ground-layer glyph sprite per non-Void tile, keyed by y * width + x.
   *  This is what the sound-hue tinting per-frame targets. */
  fgByIdx: (Sprite | null)[];
  /** Base tint captured at build time so we can restore between frames. */
  baseFgByIdx: (number | null)[];
  /** Raised (above-ground) crisp layer sprites per tile. Null for tiles
   *  with no stack (floor / cover-light). The renderer alpha-masks these
   *  each frame based on FOV so unseen walls disappear entirely. */
  raisedByIdx: (Sprite[] | null)[];
  /** Matching raised-layer halos per tile. Same FOV mask. */
  raisedHalosByIdx: (Sprite[] | null)[];
}

/** How many stack layers a given tile type has (ground layer inclusive). */
export function tileLayers(t: TileType): number {
  switch (t) {
    case TileType.Wall:
      return LAYERS.fullWall;
    case TileType.DoorClosed:
      // A shut door fills the wall opening — stacks to full wall height so its
      // '║' glyph reads as a framed doorway. Opening it drops back to 1 layer.
      return LAYERS.fullWall;
    case TileType.CubiclePartition:
      // Cubicle partitions are chest-high — same silhouette as half walls.
      return LAYERS.halfWall;
    case TileType.HalfWall:
      return LAYERS.halfWall;
    case TileType.PropLow:
      // Waist-high furniture reads at the half-wall silhouette.
      return LAYERS.halfWall;
    case TileType.PropTall:
      // Solid furniture (racks/shelving/lockers) stands chest-to-shoulder high.
      return LAYERS.halfWall;
    default:
      return LAYERS.floor;
  }
}

/**
 * How an OPEN door's `║` leaf is drawn: pivoted at one END of the leaf and swung
 * a full 90° so it reads as a door hinged on a frame post and standing wide open
 * (not merely ajar). `anchorY` is the vertical anchor of the glyph sprite (0 =
 * hinge at the north end, 1 = hinge at the south end); `rotation` is the swing.
 *
 * A DOUBLE door — two adjacent door tiles — hinges its two leaves on OPPOSITE
 * sides so they swing apart like French doors: for a horizontal pair the left
 * leaf swings west and the right leaf east; for a vertical pair the leaves hinge
 * at opposite ends and swing to opposite sides.
 */
function openDoorTransform(map: MapData, x: number, y: number): { anchorY: number; rotation: number } {
  const isDoor = (xx: number, yy: number): boolean => {
    const t = tileAt(map, xx, yy);
    return t === TileType.Door || t === TileType.DoorClosed;
  };
  const dE = isDoor(x + 1, y);
  const dW = isDoor(x - 1, y);
  const dN = isDoor(x, y - 1);
  const dS = isDoor(x, y + 1);
  const HALF = Math.PI / 2;
  // Horizontal pair: hinge both at the north end, left swings west / right east.
  if (dE && !dW) return { anchorY: 0, rotation: +HALF }; // left leaf → swings west
  if (dW && !dE) return { anchorY: 0, rotation: -HALF }; // right leaf → swings east
  // Vertical pair: hinge on opposite ends so the leaves part.
  if (dS && !dN) return { anchorY: 0, rotation: -HALF }; // top leaf, hinge north → east
  if (dN && !dS) return { anchorY: 1, rotation: -HALF }; // bottom leaf, hinge south → west
  // Single door: hinge at the north post, swing wide open to the east.
  return { anchorY: 0, rotation: -HALF };
}

/** Darken a 0xRRGGBB color by fraction f (0 = no change, 1 = black). */
function darken(color: number, f: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const k = Math.max(0, Math.min(1, 1 - f));
  return ((r * k) & 0xff) << 16 | ((g * k) & 0xff) << 8 | ((b * k) & 0xff);
}

export function buildMapLayer(
  map: MapData,
  atlas: GlyphAtlas,
  tileSize: number,
  fgLayer: Container,
  bgLayer: Container,
  bloomLayer?: Container,
): MapSprites {
  fgLayer.removeChildren();
  bgLayer.removeChildren();
  bloomLayer?.removeChildren();
  fgLayer.sortableChildren = true;
  bloomLayer && (bloomLayer.sortableChildren = true);

  const total = map.width * map.height;
  const fgByIdx: (Sprite | null)[] = new Array(total).fill(null);
  const baseFgByIdx: (number | null)[] = new Array(total).fill(null);
  const raisedByIdx: (Sprite[] | null)[] = new Array(total).fill(null);
  const raisedHalosByIdx: (Sprite[] | null)[] = new Array(total).fill(null);

  const scale = tileSize / atlas.cellH;

  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const t = tileAt(map, x, y);
      if (t === TileType.Void) continue;

      const idx = y * map.width + x;
      const px = x * tileSize;
      const py = y * tileSize;
      const cx = px + tileSize / 2;
      const cy = py + tileSize / 2;

      const bgColor = defaultBg(t);
      if (bgColor >= 0) {
        const bg = new Sprite(Texture.WHITE);
        bg.setSize(tileSize, tileSize);
        bg.tint = bgColor;
        bg.x = px;
        bg.y = py;
        bg.zIndex = y * ZY_STRIDE - 1;
        bgLayer.addChild(bg);
      }

      const glyph = map.glyphs[y * map.width + x] ?? ' ';
      if (glyph === ' ') continue;
      const tex = atlas.get(glyph) ?? Texture.EMPTY;
      const baseFg = defaultFg(t);
      const layers = tileLayers(t);

      for (let li = 0; li < layers; li++) {
        const s = new Sprite(tex);
        s.anchor.set(0.5, 0.5);
        s.scale.set(scale);
        const isGround = li === 0;
        const layerDarken = li * STACK_DARKEN_PER_LAYER;
        s.tint = isGround ? baseFg : darken(baseFg, layerDarken);
        s.alpha = isGround ? STACK_ALPHA_GROUND : STACK_ALPHA_RAISED;
        s.x = cx;
        s.y = cy; // raised layers get their screen-up offset applied per frame
        // An open door swings its `║` leaf wide open on a hinge — cosmetic only
        // (the tile stays walkable). Pivot at the hinge end (via anchorY) and
        // re-seat the sprite so that end sits on the tile edge, then swing 90°.
        if (t === TileType.Door) {
          const { anchorY, rotation } = openDoorTransform(map, x, y);
          s.anchor.set(0.5, anchorY);
          s.y = cy + (anchorY === 0 ? -tileSize / 2 : tileSize / 2);
          s.rotation = rotation;
        }
        s.zIndex = y * ZY_STRIDE + li;
        // Tag with tile-base + layer index. The renderer reads these each
        // frame to (a) offset raised layers in the direction that comes
        // out screen-up after world rotation, and (b) depth-sort by the
        // TILE's rotated-Y (not the sprite's offset position) so raised
        // stacks share a row's zIndex block.
        const tag = s as unknown as {
          __baseX: number;
          __baseY: number;
          __layerZ: number;
          __layerIdx: number;
        };
        tag.__baseX = cx;
        tag.__baseY = cy;
        tag.__layerZ = li;
        // Local layer offset large enough that raised sprites always
        // outrank their own ground tile in zIndex sort.
        tag.__layerIdx = li * 2 + 1;
        fgLayer.addChild(s);
        if (isGround) {
          fgByIdx[idx] = s;
          baseFgByIdx[idx] = baseFg;
          tag.__layerIdx = 0;
        } else {
          let list = raisedByIdx[idx];
          if (!list) {
            list = [];
            raisedByIdx[idx] = list;
          }
          list.push(s);
        }

        // Bloom halo for raised layers only (floor bloom drowns the map).
        if (bloomLayer && !isGround) {
          const h = new Sprite(tex);
          h.anchor.set(0.5, 0.5);
          h.scale.set(scale * BLOOM_SCALE);
          h.tint = s.tint;
          h.alpha = BLOOM_ALPHA * STACK_HALO_BOOST;
          h.x = cx;
          h.y = cy;
          h.blendMode = 'add';
          h.zIndex = y * ZY_STRIDE + li - 100;
          const htag = h as unknown as {
            __baseX: number;
            __baseY: number;
            __layerZ: number;
            __layerIdx: number;
          };
          htag.__baseX = cx;
          htag.__baseY = cy;
          htag.__layerZ = li;
          htag.__layerIdx = li * 2; // just below the crisp raised (+1) but above ground (0)
          bloomLayer.addChild(h);
          let hlist = raisedHalosByIdx[idx];
          if (!hlist) {
            hlist = [];
            raisedHalosByIdx[idx] = hlist;
          }
          hlist.push(h);
        }
      }
    }
  }
  return { fgByIdx, baseFgByIdx, raisedByIdx, raisedHalosByIdx };
}
